// Pure helpers for the result-entries ledger (Wave 2): swimming, gymnastics,
// rowing, golf, diving, beach_volleyball. JS port of the iOS `ResultEntryKind`.
//
// The backend (/api/result-entries) stores each row as free text (`mark_value`)
// plus a numeric shadow (`mark_numeric`) and a `lower_is_better` flag it uses to
// derive PR/SB. Those two derived-input fields are the client's job to compute,
// which is why they live here as pure, unit-tested functions.

// Sports where a smaller mark_numeric is the better result: swimming/rowing
// (time), golf (stroke score), beach volleyball (finish placement). gymnastics
// and diving are scored (higher wins).
const LOWER_IS_BETTER = new Set(['swimming', 'rowing', 'golf', 'beach_volleyball']);
// Timed sports whose marks may be entered as "mm:ss.xx".
const TIMED = new Set(['swimming', 'rowing']);

export function lowerIsBetter(slug) {
  return LOWER_IS_BETTER.has(slug);
}

// Parse the free-text mark into the numeric used only for PR/SB ranking.
// Times may be "mm:ss.xx" or plain seconds; golf is a stroke score
// ("72", relative "-4"/"+2", or "E" for even); diving/gymnastics are plain decimals; beach is a
// finish placement whose leading integer is the rank ("1st" -> 1, "T5" -> 5).
// Returns null when unparseable (backend keeps the text; row just isn't ranked).
export function parseMarkNumeric(value, slug) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;

  if (TIMED.has(slug) && s.includes(':')) {
    const parts = s.split(':').map(p => p.trim());
    if (parts.some(p => p === '' || isNaN(Number(p)))) return null;
    let seconds = 0;
    for (const n of parts.map(Number)) seconds = seconds * 60 + n;
    return Number.isFinite(seconds) ? seconds : null;
  }

  if (slug === 'golf') {
    if (s.toUpperCase() === 'E') return 0;          // even par
    const n = parseFloat(s);                        // handles "-4", "+2", "72"
    return Number.isFinite(n) ? n : null;
  }

  if (slug === 'beach_volleyball') {
    const m = /^\D*(\d+)/.exec(s);                  // leading integer of "1st"/"T5"
    return m ? parseInt(m[1], 10) : null;
  }

  const n = parseFloat(s.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

// Option lists for the per-sport context selects.
const COURSE = [
  { value: 'SCY', label: 'SCY' }, { value: 'SCM', label: 'SCM' }, { value: 'LCM', label: 'LCM' },
];
const SCALE = [
  { value: '10.0', label: '10.0' }, { value: 'fig', label: 'FIG' },
];
const WEIGHT = [
  { value: 'lightweight', label: 'Lightweight' }, { value: 'openweight', label: 'Openweight' },
];
const FORMAT = [
  { value: '6-dive', label: '6-dive' }, { value: '11-dive', label: '11-dive' },
];
const TIER = [
  { value: 'national', label: 'National' }, { value: 'regional', label: 'Regional' }, { value: 'local', label: 'Local' },
];

// Per-sport context fields. Each field: { key, label, type: 'select'|'text', options? }.
// Golf has none; beach carries two (tier select + free-text partner).
const CONTEXT = {
  swimming: [{ key: 'course', label: 'Course', type: 'select', options: COURSE }],
  gymnastics: [{ key: 'scale', label: 'Scale', type: 'select', options: SCALE }],
  rowing: [{ key: 'weight_class', label: 'Weight class', type: 'select', options: WEIGHT }],
  diving: [{ key: 'format', label: 'Format', type: 'select', options: FORMAT }],
  beach_volleyball: [
    { key: 'tier', label: 'Tier', type: 'select', options: TIER },
    { key: 'partner', label: 'Partner', type: 'text' },
  ],
};

export function contextConfig(slug) {
  return { fields: CONTEXT[slug] || [] };
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Meta-line label for a row's context, per sport. Mirrors iOS
// ResultEntryFormatting.contextLabel.
export function contextLabel(row, slug) {
  const ctx = row && row.context;
  if (!ctx) return null;
  switch (slug) {
    case 'swimming':   return ctx.course || null;
    case 'gymnastics': return ctx.scale === 'fig' ? 'FIG' : (ctx.scale || null);
    case 'rowing':     return ctx.weight_class ? cap(ctx.weight_class) : null;
    case 'diving':     return ctx.format || null;
    case 'beach_volleyball': {
      const parts = [];
      if (ctx.tier) parts.push(cap(ctx.tier));
      if (ctx.partner) parts.push(`w/ ${ctx.partner}`);
      return parts.length ? parts.join(' · ') : null;
    }
    default: return null;
  }
}
